
import React, { useState, useEffect } from 'react';
import { MessageSquare, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAnalyticsTracking } from '@/hooks/useAnalyticsTracking';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCertificationStyles } from '@/hooks/useCertificationStyles';
import { generatePulsingColor, generateBorderColor } from '@/utils/colorUtils';

interface CollapsedChatButtonProps {
  onExpand: () => void;
  errorMessage?: string;
  onDismiss?: () => void;
  disableTooltips?: boolean;
}

const CollapsedChatButton: React.FC<CollapsedChatButtonProps> = ({
  onExpand,
  errorMessage,
  onDismiss,
  disableTooltips = false,
}) => {
  const { t } = useLanguage();
  const { trackChatbotImpression, trackChatbotClick } = useAnalyticsTracking();
  const isMobile = useIsMobile();
  const { styles: certificationStyles } = useCertificationStyles();

  const [showBanner, setShowBanner] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [hasBottomBarButtons, setHasBottomBarButtons] = useState(false);

  // Dismissal is intentionally NOT persisted.
  // If a user closes the collapsed icon, it should reappear on the next page view.
  const [isDismissed, setIsDismissed] = useState(false);

  // Cleanup legacy persisted dismissal keys (older builds used storage for this)
  useEffect(() => {
    try {
      localStorage.removeItem('d2c_collapsed_chat_dismissed');
    } catch {
      // ignore
    }
    try {
      sessionStorage.removeItem('d2c_collapsed_chat_dismissed');
    } catch {
      // ignore
    }
  }, []);

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Hide immediately and prevent the banner cycle from re-showing it
    setShowBanner(false);
    setIsDismissed(true);

    onDismiss?.();
  };

  useEffect(() => {
    // Check for bottom bar buttons on mobile (skip on Safari to prevent hanging)
    if (isMobile && !/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
      const checkForBottomBarButtons = () => {
        try {
          const bottomBarWrapper = document.querySelector('.bottomBarButtonsWrapper');
          setHasBottomBarButtons(!!bottomBarWrapper);
        } catch (error) {
          // Silently handle DOM query errors
        }
      };

      checkForBottomBarButtons();

      const observer = new MutationObserver(checkForBottomBarButtons);
      observer.observe(document.body, { childList: true, subtree: true });

      return () => observer.disconnect();
    }
  }, [isMobile]);

  useEffect(() => {
    // If dismissed (or tooltips disabled), ensure the banner cannot come back
    if (disableTooltips || isDismissed) {
      setShowBanner(false);
      return;
    }

    // Track impression when component mounts
    trackChatbotImpression();

    // Start the banner cycle immediately
    setShowBanner(true);

    let hideTimeout: number | undefined;
    const scheduleHide = () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
      hideTimeout = window.setTimeout(() => {
        setShowBanner(false);
      }, 5000);
    };

    scheduleHide();

    const interval = window.setInterval(() => {
      setShowBanner(true);
      scheduleHide();
    }, 15000); // Repeat every 15 seconds (5s shown + 10s hidden)

    return () => {
      clearInterval(interval);
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
    };
  }, [trackChatbotImpression, disableTooltips, isDismissed]);

  // Determine positioning and colors based on mobile bottom bar detection
  const isReversedStyle = isMobile && hasBottomBarButtons;
  const getBottomPosition = () => {
    if (isMobile) {
      return hasBottomBarButtons ? 'bottom-[86px]' : 'bottom-[54px]'; // +30px on mobile
    }
    return 'bottom-6';
  };
  const containerClasses = `fixed right-6 z-50 ${getBottomPosition()}`;

  // Apply certification styles for collapsed chat
  const collapsedIconBg = certificationStyles.chat_collapsed_icon_bg || certificationStyles.header_bg_col;
  const collapsedIconText = certificationStyles.chat_collapsed_icon_text || certificationStyles.header_text_col;

  // Dynamically generate pulsing and border colors from the solid background color
  const pulsingColor = generatePulsingColor(collapsedIconBg, 0.2);
  const borderColor = generateBorderColor(collapsedIconBg, 0.2);

  if (isDismissed) {
    return null;
  }

  return (
    <div className={containerClasses}>
      <div
        className="relative group"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {/* Text banner - only show when not hovering */}
        <div
          className={`absolute bottom-20 right-0 transition-all duration-500 ${
            showBanner && !isHovering && !disableTooltips
              ? 'opacity-100 transform translate-y-0'
              : 'opacity-0 transform translate-y-2 pointer-events-none'
          }`}
        >
          <div
            className="px-4 py-2 shadow-xl text-sm font-medium max-w-[50vw] break-words border"
            style={{
              backgroundColor: collapsedIconBg,
              color: collapsedIconText,
              borderRadius: `${certificationStyles.corner_radius}px`,
              borderColor: borderColor,
            }}
          >
            {errorMessage || t('chat.welcome.intro')}
            <div
              className="absolute top-full right-4 w-0 h-0 d2cbot-bot_border-l-4 d2cbot-bot_border-r-4 d2cbot-bot_border-t-4 d2cbot-bot_border-transparent"
              style={{
                borderTopColor: collapsedIconBg,
              }}
            ></div>
          </div>
        </div>

        {/* Pulsing ring animation - dynamically derived from solid color */}
        <div
          className="absolute inset-0 w-16 h-16 animate-ping"
          style={{
            backgroundColor: pulsingColor,
            borderRadius: `${certificationStyles.corner_radius}px`,
          }}
        ></div>
        <div
          className="absolute inset-0 w-16 h-16 animate-pulse"
          style={{
            backgroundColor: pulsingColor,
            borderRadius: `${certificationStyles.corner_radius}px`,
          }}
        ></div>

        {/* Dismiss button - reversed colors from main icon */}
        <button
          onClick={handleDismiss}
          className="chatbot-destroy-btn absolute -top-3 -right-3 w-10 h-10 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center z-10 border hover:scale-110 hover:opacity-80"
          style={{
            backgroundColor: collapsedIconText,
            color: collapsedIconBg,
            borderColor: collapsedIconBg,
          }}
          aria-label="Dismiss chat"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Main button */}
        <Button
          onClick={() => {
            trackChatbotClick();
            onExpand();
          }}
          className="chatbot-open-btn relative w-16 h-16 shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center hover:scale-110 transform border-2"
          style={{
            backgroundColor: collapsedIconBg,
            color: collapsedIconText,
            borderRadius: `${certificationStyles.corner_radius}px`,
            borderColor: borderColor,
          }}
        >
          <div className="flex items-center justify-center space-x-1">
            <MessageSquare
              size={24}
              style={{ width: 24, height: 24, minWidth: 24, minHeight: 24 }}
              className="d2cbot-icon-24 group-hover:scale-110 transition-transform duration-200"
            />
            <Sparkles
              size={16}
              style={{ width: 16, height: 16, minWidth: 16, minHeight: 16 }}
              className="d2cbot-icon-16 animate-pulse text-yellow-300"
            />
          </div>
        </Button>

        {/* Hover tooltip - only show when hovering and banner is not visible */}
        <div
          className={`absolute bottom-full right-0 mb-2 px-3 py-2 text-sm shadow-lg transition-opacity duration-200 pointer-events-none max-w-[50vw] break-words border ${
            isHovering && !showBanner && !disableTooltips ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            backgroundColor: collapsedIconBg,
            color: collapsedIconText,
            borderRadius: `${certificationStyles.corner_radius}px`,
            borderColor: borderColor,
          }}
        >
          <div className="flex items-center space-x-1">
            <span>{errorMessage || t('chat.welcome.intro')}</span>
            {!errorMessage && <Sparkles className="w-3 h-3 text-yellow-300" />}
          </div>
          <div
            className="absolute top-full right-4 w-0 h-0 d2cbot-bot_border-l-4 d2cbot-bot_border-r-4 d2cbot-bot_border-t-4 d2cbot-bot_border-transparent"
            style={{
              borderTopColor: collapsedIconBg,
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default CollapsedChatButton;
